import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Box, IconButton, Stack, Typography } from '@mui/material';
import { Notifications, NotificationsOff } from '@mui/icons-material';
import { useNotificationsContext } from '../../../context/NotificationsContext';
import CustomTooltip from '../../shared/atoms/CustomTooltip';
import { connectionColorObj } from '../../../themes';

const NotificationConnection = ({ switchNotification, containerState }) => {
  const { connection, notifications } = useNotificationsContext();
  const { t, i18n } = useTranslation();

  const isConnecting = useMemo(
    () => connection.status === 'Connecting',
    [connection.status],
  );

  const isError = useMemo(
    () =>
      connection.status === 'Error' ||
      connection.status === 'ConnectionDisrupted',
    [connection.status],
  );

  const displayConnection = useMemo(
    () => (
      <Stack>
        <Typography>
          {t('components.notification_container.tooltip.title')}
        </Typography>
        <Typography sx={{ paddingTop: '10px' }}>
          {t('components.notification_container.tooltip.status', {
            status: connection.status,
          })}
        </Typography>
        <Typography>
          {connection.updated.toLocaleString(i18n.language)}
        </Typography>
        {!isConnecting && !isError && (
          <Typography sx={{ paddingTop: '10px' }}>
            {t('components.notification_container.tooltip.click', {
              status: containerState,
            })}
          </Typography>
        )}
        {isError && (
          <Typography sx={{ paddingTop: '10px' }}>
            {t('components.notification_container.tooltip.contact')}
          </Typography>
        )}
      </Stack>
    ),
    [
      connection.status,
      connection.updated,
      containerState,
      i18n.language,
      isConnecting,
      isError,
      t,
    ],
  );

  return (
    <CustomTooltip
      title={displayConnection}
      placement="left"
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 75],
              },
            },
          ],
        },
      }}
    >
      <Box>
        <IconButton
          size="large"
          aria-label="show new notifications"
          sx={{
            color: `${
              connectionColorObj[connection.status] ||
              connectionColorObj.Disconnected
            } !important`,
          }}
          onClick={switchNotification}
        >
          <Badge badgeContent={notifications.length} color="error">
            {connection.status === 'Connected' ? (
              <Notifications sx={{ fontSize: { xs: 20, sm: 24 } }} />
            ) : (
              <NotificationsOff sx={{ fontSize: { xs: 20, sm: 24 } }} />
            )}
          </Badge>
        </IconButton>
      </Box>
    </CustomTooltip>
  );
};

NotificationConnection.propTypes = {
  switchNotification: PropTypes.func.isRequired,
  containerState: PropTypes.bool.isRequired,
};

export default NotificationConnection;
